import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { isTestProcessMode, ProcessReleaseSelectionModeEnum } from "@ankaadia/ankaadia-shared";
import { TranslocoService } from "@jsverse/transloco";
import { Message } from "primeng/message";
import { Tag } from "primeng/tag";
import { Tooltip } from "primeng/tooltip";
import { translateSafely } from "../directives/transloco/translate-safely";

/**
 * Everything the badge needs from a process to decide whether it is a test process (AB#21628).
 *
 * Declared structurally instead of taking the frontend's generated `ProcessFragment`: this library
 * cannot see `@ankaadia/graphql`, and the generated `ProcessReleaseSelectionModeEnum` is a *separate*
 * enum declaration from the shared one, so the two are not assignable to each other even though they
 * carry the same values. The template literal type `${ProcessReleaseSelectionModeEnum}` accepts
 * members of both, which keeps the call sites free of casts.
 */
export interface TestProcessBadgeProcess {
  releaseSelectionMode?: `${ProcessReleaseSelectionModeEnum}` | null;
}

/** How prominent the marker is rendered. */
export type TestProcessBadgeVariant = "tag" | "banner";

/**
 * Who reads the marker. `internal` names the release selection mode, `candidate` avoids that jargon
 * and only says that this is a test run (AB#21628) - a test candidate works in the process like in
 * any other, so the wording has to make sense without knowing what a process model release is.
 */
export type TestProcessAudience = "internal" | "candidate";

/**
 * Whether a process runs on something other than the published release of its model, i.e. whether it
 * is a test process (AB#21628). Thin wrapper around the shared {@link isTestProcessMode} that takes
 * the structural {@link TestProcessBadgeProcess} shape, so a generated `ProcessFragment` can be passed
 * without casting the generated enum to the shared one. Use it wherever the marker cannot be a
 * component, e.g. when building table tags.
 */
export function isTestProcess(process: TestProcessBadgeProcess): boolean {
  return isTestProcessMode({ releaseSelectionMode: process?.releaseSelectionMode as ProcessReleaseSelectionModeEnum });
}

/** The short marker text, e.g. for a tag. */
export function testProcessLabel(audience: TestProcessAudience = "internal", language?: string): string {
  return translateSafely(
    audience === "candidate" ? "process.testProcessCandidateShort" : "process.testProcessShort",
    null,
    language
  );
}

/** The full marker wording: the tooltip of a tag and the text of a banner. */
export function testProcessDescription(audience: TestProcessAudience = "internal", language?: string): string {
  return translateSafely(
    audience === "candidate" ? "process.testProcessCandidate" : "process.testProcess",
    null,
    language
  );
}

/**
 * Marks a process that does not run on the published release of its model, i.e. one that may execute
 * a model version nobody released for productive use (AB#21628). Test processes are never hidden -
 * they stay fully visible for users and candidates alike - so this marker is the only thing telling
 * someone that what they are looking at is a test run. Render it wherever a process or one of its
 * tasks is shown; a task never carries the flag itself, it is always derived from its process.
 *
 * `tag` is the compact form for table cells and row badges, `banner` the prominent one for detail
 * headers. The component renders nothing at all for a normal (published) process.
 */
@Component({
  selector: "new-ui-test-process-badge",
  templateUrl: "./new-ui-test-process-badge.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Tag, Message, Tooltip],
})
export class NewUiTestProcessBadgeComponent {
  private readonly transloco = inject(TranslocoService);

  /**
   * `translateSafely` reads the active language once per call, so the computed labels below would
   * keep the wording of the language that was active when they were first evaluated. Tracking the
   * language as a signal re-evaluates them on every language switch.
   */
  private readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  /** The process to mark - anything that carries the release selection, e.g. a `ProcessFragment`. */
  readonly process = input.required<TestProcessBadgeProcess>();

  readonly variant = input<TestProcessBadgeVariant>("tag");

  /** Candidate-facing wording leaves out everything about releases - see {@link TestProcessAudience}. */
  readonly audience = input<TestProcessAudience>("internal");

  protected readonly isTestProcess = computed(() => isTestProcess(this.process()));

  protected readonly label = computed(() => testProcessLabel(this.audience(), this.activeLanguage()));

  /** Full wording: the tooltip of the compact tag and the text of the banner. */
  protected readonly description = computed(() => testProcessDescription(this.audience(), this.activeLanguage()));
}
